
































































import { Component, Prop, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { BaseMixin } from '@/mixins/BaseMixin';
import { FormMixin } from '@/mixins/FormMixin';
import { XIcon } from 'vue-feather-icons';
import FormRow from '@/components/common/forms/FormRow.vue';
import FormInput from '@/components/common/forms/FormInput.vue';
import FormSubmit from '@/components/common/forms/FormSubmit.vue';
import { ChecklistApi } from '@/services/ChecklistApi';
import { UpdateChecklistDto, IChecklistItem, CreateChecklistDto, IDamage } from 'admiral-core';
import { BModal } from 'bootstrap-vue';

export type EditMode = 'item' | 'item-edit' | 'title' | 'create';

export interface ChecklistEditData {
  mode: EditMode;
  checklistId?: string;
  currentValue: string;
  checklist?: any;
  itemId?: string;
  damageId?: string;
  taskId?: string;
}

@Component({
  name: 'checklist-edit-modal',
  components: {
    XIcon,
    FormRow,
    FormInput,
    FormSubmit,
  },
})
export default class ChecklistEditModal extends mixins(BaseMixin, FormMixin) {
  @Prop({ required: false, type: Object })
  editData!: ChecklistEditData | null;

  inputValue = '';
  originalValue = '';

  get modalTitle(): string {
    if (!this.editData) return '';
    switch (this.editData.mode) {
      case 'item':
        return 'Aufgabe hinzufügen';
      case 'item-edit':
        return 'Aufgabe bearbeiten';
      case 'title':
        return 'Titel bearbeiten';
      case 'create':
        return 'Checkliste hinzufügen';
      default:
        return '';
    }
  }

  get inputPlaceholder(): string {
    if (!this.editData) return '';
    switch (this.editData.mode) {
      case 'item':
        return 'Neue Aufgabe eingeben';
      case 'item-edit':
        return 'Aufgabe bearbeiten';
      case 'title':
        return 'Titel eingeben';
      case 'create':
        return 'Checkliste hinzufügen';
      default:
        return '';
    }
  }

  get canSave(): boolean {
    const trimmedValue = (this.inputValue || '').trim();

    if (!trimmedValue) {
      return false;
    }

    if (this.editData?.mode === 'title' || this.editData?.mode === 'item-edit') {
      return trimmedValue !== this.originalValue.trim();
    }

    return true;
  }

  @Watch('editData', { immediate: true })
  onEditDataChanged() {
    if (this.editData) {
      this.inputValue = this.editData.currentValue || '';
      this.originalValue = this.editData.currentValue || '';
      this.resetErrors();
    }
  }

  mounted() {
    this.$root.$on('bv::modal::shown', (bvEvent: Event, modalId: string) => {
      if (modalId === 'checklist-edit-modal') {
        this.focusInput();
      }
    });
  }

  beforeDestroy() {
    this.$root.$off('bv::modal::shown');
  }

  focusInput() {
    this.$nextTick(() => {
      const input = this.$refs.textInput as any;
      if (input && input.$refs && input.$refs.input) {
        input.$refs.input.focus();
        if (this.editData?.mode === 'title' || this.editData?.mode === 'item-edit') {
          const inputElement = input.$refs.input;
          const textLength = inputElement.value.length;
          inputElement.setSelectionRange(textLength, textLength);
        }
      }
    });
  }

  onInput(value: string) {
    this.inputValue = value;
    this.resetError('text');
  }

  async save() {
    const trimmedValue = (this.inputValue || '').trim();

    if (!trimmedValue) {
      this.addError(true, 'text', 'Bitte geben Sie einen Text ein');
      return;
    }

    if (!this.editData) {
      return;
    }

    this.resetErrors();

    try {
      this.isLoading = true;

      if (this.editData.mode === 'item') {
        await this.addItem(trimmedValue);
      } else if (this.editData.mode === 'item-edit') {
        await this.editItem(trimmedValue);
      } else if (this.editData.mode === 'create') {
        await this.createChecklist(trimmedValue);
      } else {
        await this.updateTitle(trimmedValue);
      }

      this.isDone = true;

      setTimeout(() => {
        (this.$refs['checklist-edit-modal'] as BModal).hide();
      }, 500);
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      let errorMessage = 'Fehler beim Speichern';
      switch (this.editData.mode) {
        case 'item':
          errorMessage = 'Fehler beim Hinzufügen der Aufgabe';
          break;
        case 'item-edit':
          errorMessage = 'Fehler beim Aktualisieren der Aufgabe';
          break;
        case 'title':
          errorMessage = 'Fehler beim Aktualisieren des Titels';
          break;
        case 'create':
          errorMessage = 'Fehler beim Erstellen der Checkliste';
          break;
      }
      this.$toastr.e(errorMessage);
    } finally {
      this.isLoading = false;
    }
  }

  async addItem(text: string) {
    if (!this.editData?.checklist || !this.editData?.checklistId) return;

    const checklist = this.editData.checklist;
    const checklistId = this.editData.checklistId;
    const newItem: IChecklistItem = {
      id: this.generateId(),
      text,
      done: false,
    };

    const updatedItems = [...(checklist.items || []), newItem];

    const updateDto: UpdateChecklistDto = {
      agency: this.agency,
      title: checklist.title,
      items: updatedItems,
    };

    await ChecklistApi.update(checklistId, updateDto);

    this.$emit('item-added', {
      checklistId,
      newItem,
      updatedItems,
    });

    this.$toastr.s('Aufgabe erfolgreich hinzugefügt');
  }

  async editItem(text: string) {
    if (!this.editData?.checklist || !this.editData?.itemId || !this.editData?.checklistId) return;

    const checklist = this.editData.checklist;
    const checklistId = this.editData.checklistId;
    const itemId = this.editData.itemId;
    const updatedItems = (checklist.items || []).map((item: IChecklistItem) =>
      item.id === itemId ? { ...item, text } : item,
    );

    const updateDto: UpdateChecklistDto = {
      agency: this.agency,
      title: checklist.title,
      items: updatedItems,
    };

    await ChecklistApi.update(checklistId, updateDto);

    this.$emit('item-updated', {
      checklistId,
      itemId,
      newText: text,
      updatedItems,
    });

    this.$toastr.s('Aufgabe erfolgreich aktualisiert');
  }

  async updateTitle(title: string) {
    if (!this.editData?.checklist || !this.editData?.checklistId) return;

    const checklist = this.editData.checklist;

    const updateDto: UpdateChecklistDto = {
      agency: this.agency,
      title,
      items: checklist.items || [],
    };

    await ChecklistApi.update(this.editData.checklistId, updateDto);

    this.$emit('title-updated', {
      checklistId: this.editData.checklistId,
      newTitle: title,
    });

    this.$toastr.s('Titel erfolgreich aktualisiert');
  }

  async createChecklist(title: string) {
    const createDto: CreateChecklistDto = {
      agency: this.agency,
      title,
      items: [],
      isTemplate: false,
      damage: this.editData?.damageId ? ({ id: this.editData.damageId } as IDamage) : undefined,
      task: this.editData?.taskId ? { id: this.editData.taskId } : undefined,
    };

    const { data } = await ChecklistApi.create(createDto);

    this.$emit('checklist-created', {
      checklist: { ...data, items: data.items || [] },
    });

    this.$toastr.s('Checkliste erfolgreich erstellt');
  }

  generateId(): string {
    return 'item_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  cancel() {
    this.resetForm();
  }

  onModalHidden() {
    this.resetForm();
  }

  resetForm() {
    this.inputValue = '';
    this.originalValue = '';
    this.resetErrors();
    this.isLoading = false;
    this.isDone = false;
  }
}
